import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "@jsq/ds/core";
import { useRootProfileContext } from "../../contexts/RootProfileContext";
import { useUpdateIndividualProfileMutation } from "../../graphql/supergraph.index";
import useProfile from "../../hooks/profile/useProfile";
import { IndividualForm } from "../IndividualForm";
import { IndividualProfile } from "../IndividualForm/types";
import { IndividualProfileEditScreenProps } from "./types";

/**
 * `IndividualProfileEditScreen` is a container component that wraps the `IndividualForm` component
 * and adds the business logic for submitting a GraphQL mutation when the form is submitted.
 *
 * This component handles:
 * 1. Loading the individual profile data
 * 2. Transforming the form data to match the GraphQL mutation input
 * 3. Submitting the mutation
 * 4. Handling success and error states
 * 5. Navigation after form submission
 */
export const IndividualProfileEditScreen: React.FC<IndividualProfileEditScreenProps> = ({
  caseResult,
  basePath,
}) => {
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId } = useRootProfileContext();
  const isRoot = Number(profileId) === rootProfileId;
  const history = useHistory();

  const caseId = caseResult?.complianceCasesByObjectId[0].id || -1;

  const profile = useProfile(caseResult);

  // Get the mutation function from the generated GraphQL code
  const [updateIndividualProfile] = useUpdateIndividualProfileMutation();

  if (!profile) {
    throw new Error("Profile not found");
  }

  // Handle form submission
  const handleFormSubmit = async (values: IndividualProfile) => {
    try {
      // Format the date for the API
      const formattedDateOfBirth = values.dateOfBirth
        ? new Date(values.dateOfBirth).toISOString().split("T")[0]
        : null;

      // Create the address object
      const address = {
        street: values.streetLine1,
        street2: values.streetLine2 || null,
        city: values.city,
        state: values.state,
        province: values.province || null,
        postalCode: values.zip || null,
        country: values.country,
      };

      // Only include taxId if it has changed from the initial value
      const taxId =
        values.identificationNumber !== initialValues.identificationNumber
          ? values.identificationNumber || null
          : undefined;

      await updateIndividualProfile({
        variables: {
          profileId: parseInt(profileId),
          firstName: values.firstName,
          middleName: values.middleName || null,
          lastName: values.lastName,
          dateOfBirth: formattedDateOfBirth,
          ...(taxId !== undefined && { taxId }),
          addresses: [address],
          caseId: caseId,
        },
        onCompleted: () => {
          toast.success("Individual profile updated successfully");
          history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
        },
        onError: (error: Error) => {
          toast.error(`Error updating individual profile: ${error.message}`);
        },
        refetchQueries: ["GetComplianceCasesByObjectId"],
        awaitRefetchQueries: true,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Error updating individual profile: ${errorMessage}`);
    }
  };

  // Handle back button click
  const handleBack = () => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  };

  // Transform the profile data to match the form's initial values
  const initialValues: IndividualProfile = {
    firstName: profile.firstName || "",
    middleName: profile.middleName || "",
    lastName: profile.lastName || "",
    dateOfBirth: profile.dateOfBirth ? new Date(profile.dateOfBirth) : null,
    identificationNumber: profile.taxIdUnencryptedLast4Digits || "",
    streetLine1: profile.addresses?.[0]?.street || "",
    streetLine2: profile.addresses?.[0]?.street2 || "",
    city: profile.addresses?.[0]?.city || "",
    state: profile.addresses?.[0]?.state?.code || "",
    province: profile.addresses?.[0]?.province || "",
    zip: profile.addresses?.[0]?.postalCode || "",
    country: profile.addresses?.[0]?.country?.alpha2code || "",
  };

  return (
    <IndividualForm
      initialValues={initialValues}
      onContinue={handleFormSubmit}
      onBack={handleBack}
      value={""}
    />
  );
};
